// Client role labels — Users & Roles → Roles tab. Admin-managed picklist of
// friendly job-title labels (e.g. "Project Manager") that can be assigned to
// portal users on top of their technical permission role. Director/admin only.
// Deleting a label only removes it from the picker; any user carrying it has
// role_label_id cleared (their real permission role is unaffected).
import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Save, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import RoleChip from "@/components/RoleChip";
import { ROLE_LABELS } from "@/lib/constants";

const PORTAL_ROLES = ["client_admin", "site_manager", "reviewer"];
const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,#C75A1B)]";

export default function RoleLabelsPanel({ onChange }) {
    const [labels, setLabels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(null); // { mode: create|edit, l? }

    function load() {
        setLoading(true);
        api.get("/role-labels").then((r) => { setLabels(r.data); onChange?.(r.data); })
            .catch(() => toast.error("Could not load client roles"))
            .finally(() => setLoading(false));
    }
    useEffect(() => { load(); }, []);

    async function remove(l) {
        if (!window.confirm(`Remove "${l.name}" from the Client role picklist? Any user currently carrying it keeps their real permission role but loses the label.`)) return;
        try { await api.delete(`/role-labels/${l.id}`); toast.success("Removed"); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
    }

    return (
        <div data-testid="role-labels-panel" className="space-y-4">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <p className="text-[13px] max-w-lg" style={{ color: "#6F6A64" }}>
                    Friendly job-title labels admins can assign to a client-portal user on top of their permission role (Client Admin / Site Manager / Reviewer). Renaming or re-mapping a label here updates it everywhere it's used.
                </p>
                <button data-testid="role-label-new" onClick={() => setDialog({ mode: "create" })}
                    className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white shrink-0" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                    <Plus size={15} /> New client role
                </button>
            </div>

            {loading ? (
                <div className="py-16 text-center text-[#9A968F]"><Loader2 className="inline animate-spin mr-2" size={16} />Loading…</div>
            ) : labels.length === 0 ? (
                <div className="py-16 text-center text-[#9A968F] border border-dashed rounded-xl" style={{ borderColor: "#E5E0DA" }}>No client roles yet. Add your first one.</div>
            ) : (
                <div className="bg-white border rounded-[12px] shadow-brand-sm divide-y" style={{ borderColor: "#E5E0DA" }}>
                    {labels.map((l) => (
                        <div key={l.id} data-testid={`role-label-row-${l.id}`} className="flex items-center gap-3 px-4 py-3">
                            <ShieldCheck size={15} className="text-[#9F4212] shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-[#1F1F1F] text-[13.5px]">{l.name}</div>
                                <div className="text-[11px] mt-0.5" style={{ color: "#6F6A64" }}>
                                    Permissions: <RoleChip role={l.base_role} />
                                </div>
                            </div>
                            <button data-testid={`role-label-edit-${l.id}`} onClick={() => setDialog({ mode: "edit", l })}
                                className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md border hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>
                                <Pencil size={12} /> Edit
                            </button>
                            <button data-testid={`role-label-delete-${l.id}`} onClick={() => remove(l)}
                                className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md text-[#B33A2E] hover:bg-[#FBE8E5]">
                                <Trash2 size={12} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {dialog && <LabelDialog mode={dialog.mode} initial={dialog.l} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
        </div>
    );
}

function LabelDialog({ mode, initial, onClose, onSaved }) {
    const [name, setName] = useState(initial?.name || "");
    const [baseRole, setBaseRole] = useState(initial?.base_role || "site_manager");
    const [busy, setBusy] = useState(false);

    async function save() {
        if (!name.trim()) { toast.error("Name is required"); return; }
        setBusy(true);
        try {
            const body = { name: name.trim(), base_role: baseRole };
            if (mode === "create") await api.post("/role-labels", body);
            else await api.patch(`/role-labels/${initial.id}`, body);
            toast.success(mode === "create" ? "Client role created" : "Client role saved");
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
        setBusy(false);
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
            <div data-testid="role-label-dialog" className="bg-white rounded-[14px] shadow-xl w-full max-w-md border" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "#E5E0DA" }}>
                    <h3 className="text-base font-bold text-[#1F1F1F]">{mode === "create" ? "New client role" : "Edit client role"}</h3>
                    <button data-testid="role-label-dialog-close" onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X size={16} /></button>
                </div>
                <div className="p-5 space-y-4">
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] block">Name
                        <input data-testid="role-label-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Contracts Manager" className={inputCls + " mt-1 font-normal normal-case"} /></label>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] block">Permissions come from
                        <select data-testid="role-label-base-role" value={baseRole} onChange={(e) => setBaseRole(e.target.value)} className={inputCls + " mt-1 font-normal normal-case"}>
                            {PORTAL_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                        </select>
                    </label>
                    <p className="text-[11px]" style={{ color: "#9A968F" }}>Users given this label will have exactly the same access as {ROLE_LABELS[baseRole]}.</p>
                </div>
                <div className="px-5 py-3 border-t flex items-center justify-end gap-2 bg-[#F5F3F0]" style={{ borderColor: "#E5E0DA" }}>
                    <button onClick={onClose} className="px-3 py-2 text-sm text-[#6F6A64]">Cancel</button>
                    <button data-testid="role-label-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-60" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {mode === "create" ? "Create" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}
